// ESC/POS barcode-label builder.
//
// The TSPL builder (tspl.ts) only speaks to TSC-style label printers. Most POS
// setups print labels on the same ESC/POS thermal printer that prints receipts,
// which ignores TSPL entirely (nothing comes out). This produces the same label
// content — header, name, content rows, real scannable bars — as ESC/POS
// commands: GS k for the barcode, centred text around it.

import { LabelSpec } from './tspl';

const ESC = 0x1b;
const GS = 0x1d;

function asciize(s: string): string {
  return s
    .replace(/₹/g, 'Rs')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, '')
    .trim();
}

class Bytes {
  private b: number[] = [];

  raw(...v: number[]): this {
    this.b.push(...v);
    return this;
  }

  text(s: string): this {
    this.b.push(...Array.from(new TextEncoder().encode(asciize(s))));
    return this;
  }

  line(s = ''): this {
    return this.text(s).raw(0x0a);
  }

  done(): Uint8Array {
    return new Uint8Array(this.b);
  }
}

// Barcode symbology for a value: EAN-13 when it is 12/13 digits (so retail
// scanners read it as a product code), CODE128 otherwise.
function symbology(code: string): { m: number; data: string } {
  if (/^\d{12,13}$/.test(code)) return { m: 67, data: code.slice(0, 12) }; // EAN13, printer adds check digit
  if (/^\d{7,8}$/.test(code)) return { m: 68, data: code.slice(0, 7) }; // EAN8
  return { m: 73, data: `{B${code}` }; // CODE128, code set B
}

// 203 dpi: 8 dots per mm; Font A glyphs are 12 dots wide.
const DOTS_PER_MM = 8;
const CHAR_DOTS = 12;

// How many Font A characters fit across the configured label width.
function charsPerLine(widthMm: number): number {
  return Math.max(8, Math.floor((widthMm * DOTS_PER_MM) / CHAR_DOTS));
}

// Widest module width (in dots) that keeps the symbol inside the label.
// CODE128 is 11 modules per encoded character plus start/checksum/stop and
// quiet zones; EAN-13 is a fixed 95 modules.
function fitModuleWidth(code: string, widthMm: number, m: number): number {
  const usable = widthMm * DOTS_PER_MM * 0.92; // leave a quiet zone
  const modules = m === 73 ? 11 * (code.length + 3) + 13 : m === 67 ? 113 : 81;
  for (let w = 4; w > 2; w -= 1) if (modules * w <= usable) return w;
  return 2;
}

// GS k <m> <n> d1..dn  (function B — length-prefixed, works on every modern
// ESC/POS printer; the NUL-terminated function A is legacy).
function barcode(out: Bytes, code: string, heightDots: number, moduleWidth: number): number {
  const { m, data } = symbology(asciize(code).replace(/\s+/g, ''));
  const bytes = Array.from(new TextEncoder().encode(data));
  out
    .raw(GS, 0x68, Math.max(1, Math.min(255, heightDots))) // GS h — bar height
    .raw(GS, 0x77, Math.max(2, Math.min(6, moduleWidth))) // GS w — module width
    .raw(GS, 0x48, 0x02) // GS H — human-readable text below the bars
    .raw(GS, 0x66, 0x00) // GS f — HRI font A
    .raw(GS, 0x6b, m, bytes.length, ...bytes)
    .raw(0x0a);
  return m;
}

export interface EscPosLabelOptions {
  /** Bar height in dots (203 dpi ≈ 8 dots/mm). Derived from the label height. */
  barHeight?: number;
  /** Narrow-bar width in dots, 2–6. */
  moduleWidth?: number;
  /** Cut after each label (label printers with a cutter). */
  cut?: boolean;
}

export function buildLabelEscPos(spec: LabelSpec, opts: EscPosLabelOptions = {}): Uint8Array {
  const out = new Bytes();
  // Bars get roughly a third of the label height, clamped to a sane range.
  // Bars get roughly 40% of the configured label height; text is clipped to the
  // configured label width, so the label matches the size chosen in settings.
  const barHeight = opts.barHeight ?? Math.max(40, Math.min(160, Math.round(spec.heightMm * DOTS_PER_MM * 0.4)));
  const cleanCode = asciize(spec.code).replace(/\s+/g, '');
  const moduleWidth =
    opts.moduleWidth ??
    fitModuleWidth(
      cleanCode,
      Math.max(8, spec.widthMm - (spec.marginLeftMm ?? 0) - (spec.marginRightMm ?? 0)),
      symbology(cleanCode).m
    );
  const leftMarginDots = Math.round((spec.marginLeftMm ?? 0) * DOTS_PER_MM);
  const topMarginDots = Math.round((spec.marginTopMm ?? 0) * DOTS_PER_MM);
  const bottomMarginDots = Math.round((spec.marginBottomMm ?? 0) * DOTS_PER_MM);
  // Both side margins eat into the printable width.
  const width = charsPerLine(
    Math.max(8, spec.widthMm - (spec.marginLeftMm ?? 0) - (spec.marginRightMm ?? 0))
  );
  const clip = (s: string, max: number) => (s.length > max ? s.slice(0, max) : s);
  const fit = (s: string) => clip(s, width);

  const emitText = () => {
    if (spec.header) out.raw(ESC, 0x45, 1).line(fit(spec.header)).raw(ESC, 0x45, 0);
    if (spec.name) out.line(fit(spec.name));
    spec.rows.forEach((r) => {
      // GS ! sets the width/height multiplier; a scaled line fits proportionally
      // fewer characters across the label. Rather than chopping a big Price in
      // half, drop the "Price:" title first, then step the size down until the
      // value fits the label width.
      let scale = Math.max(1, Math.min(4, r.scale ?? 1));
      let text = `${r.label}: ${r.value}`;
      if (text.length * scale > width) text = r.value;
      while (scale > 1 && text.length * scale > width) scale -= 1;
      const n = ((scale - 1) << 4) | (scale - 1);
      if (scale > 1) out.raw(GS, 0x21, n);
      if (r.bold) out.raw(ESC, 0x45, 1);
      out.line(clip(text, Math.floor(width / scale)));
      if (r.bold) out.raw(ESC, 0x45, 0);
      if (scale > 1) out.raw(GS, 0x21, 0);
    });
    if (spec.footer) out.line(fit(spec.footer));
  };
  const emitBars = () => barcode(out, spec.code, barHeight, moduleWidth);

  const copies = Math.max(1, spec.copies);
  for (let i = 0; i < copies; i += 1) {
    out.raw(ESC, 0x40); // init
    // GS L — left margin, in dots; then a dot-feed for the top margin.
    if (leftMarginDots > 0) out.raw(GS, 0x4c, leftMarginDots & 0xff, (leftMarginDots >> 8) & 0xff);
    if (topMarginDots > 0) out.raw(ESC, 0x4a, Math.min(255, topMarginDots));
    out.raw(ESC, 0x61, 0x01); // centre everything within the printable area
    if (spec.position === 'Top') {
      emitBars();
      emitText();
    } else {
      emitText();
      emitBars();
    }
    // Bottom margin, then the gap before the next label.
    if (bottomMarginDots > 0) out.raw(ESC, 0x4a, Math.min(255, bottomMarginDots));
    out.raw(ESC, 0x64, 0x01);
    if (opts.cut) out.raw(ESC, 0x64, 0x02).raw(GS, 0x56, 0x42, 0x00);
  }
  // Feed the finished label clear of the print head. Without this the last
  // label sits in the printer's buffer until the *next* job pushes it out —
  // which is why a label seemed to need a second tap on the Print button,
  // while receipts (which end in a cut) always came out first time.
  out.raw(ESC, 0x64, 0x04);
  return out.done();
}
